// SVSchreiberPage.jsx — SV-Wertungsportal (Dark Theme)
// Verteidiger = benannte Person aus Teilnehmerliste
// Angreifer   = anonymes Score-Panel (keine Personenauswahl)
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  sendeAnLeinwand, schlankerTeilnehmer, schlankeRangliste, schlankeStarterliste, schlankeFolgende,
  oeffneLeinwand, kopiereLeinwandAdresse, setzeLeinwandZiel,
} from '../lib/leinwand';
import { useSearchParams } from 'react-router-dom';
import { toast } from '../components/common/Toast';
import WeiterHinweis from '../components/dashboard/WeiterHinweis';
import useAufrufFolgen from '../hooks/useAufrufFolgen';
import { ladeEhrung } from '../lib/siegerEhrung';
import { SIEGER_ABLAUF_MS } from '../components/leinwand/SiegerBuehne';

const API_URL = process.env.REACT_APP_API_URL || '';

const SURFACE  = '#111118';
const SURFACE2 = '#1a1a26';
const BORDER   = 'rgba(255,255,255,0.09)';
const VERT_COL = '#7c3aed';
const VERT_BG  = 'rgba(124,58,237,0.12)';
const VERT_BD  = 'rgba(124,58,237,0.35)';
const ANG_COL  = '#ef4444';
const ANG_BD   = 'rgba(239,68,68,0.35)';
const GREEN    = '#10b981';

// Wieviele Uebungen eine SV-Kategorie hat, steht an der Kategorie
// (formen_kategorien.anzahl_kampfrichter — bei SV ist das die Zahl der
// Uebungen). Hier stand bisher fest 5, unabhaengig davon. Folge: In einer
// Kategorie mit drei Uebungen liess sich Uebung 4 werten, und die Wertung
// zaehlte zu einem Satz, den es nicht gibt. Genau so steht es seit dem
// 11.05.2026 in der Produktion (Kategorie 112, Uebung 4 von 3).
const UEBUNGEN_VORGABE = 5;

const LEVEL_CFG = {
  farb:    { label: '🟡 Farbgurt',    presets: [6.0, 6.5, 7.0, 7.5, 8.0],   default: 7.0 },
  schwarz: { label: '⬛ Schwarzgurt', presets: [8.0, 8.5, 9.0, 9.5, 10.0], default: 9.0 },
};

function getSVSubTyp(divCode = '') {
  const c = (divCode || '').toUpperCase();
  if (c.startsWith('RSD')) return 'Real Self Defense';
  if (c.startsWith('TSD')) return 'Team Self Defense';
  if (c.startsWith('SD'))  return '1 vs. 1';
  return 'Selbstverteidigung';
}

function formatTime(s) {
  const sec = Math.max(0, Math.floor(s));
  return `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;
}

/* ── Angreifer Score-Panel (anonym, keine Personenauswahl) ── */
function AngreiferPanel({ level, activeUebung, score, onScoreChange, saved, disabled }) {
  const lc = LEVEL_CFG[level];

  return (
    <div style={{
      background: SURFACE, border: `1px solid ${ANG_BD}`, borderRadius: 14,
      padding: '1.1rem 1.25rem', display: 'flex', flexDirection: 'column', gap: '0.85rem',
      flex: 1, minWidth: 0,
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <span style={{ fontSize: '1.1rem' }}>⚔️</span>
        <span style={{ fontWeight: 700, color: ANG_COL, fontSize: '0.9rem', letterSpacing: '0.04em' }}>ANGREIFER</span>
        {saved && (
          <span style={{ marginLeft: 'auto', fontSize: '0.72rem', color: GREEN, background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: 5, padding: '0.1rem 0.45rem' }}>
            ✓ Gespeichert
          </span>
        )}
      </div>

      {/* Score-Anzeige */}
      <div style={{ textAlign: 'center', fontSize: '3.5rem', fontWeight: 900, color: ANG_COL, lineHeight: 1, padding: '0.5rem 0' }}>
        {score.toFixed(1)}
      </div>

      {/* Preset-Buttons */}
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${lc.presets.length}, 1fr)`, gap: '0.45rem' }}>
        {lc.presets.map(p => {
          const isAct = Math.abs(p - score) < 0.01;
          return (
            <button key={p} disabled={disabled}
              onClick={() => onScoreChange(p)}
              style={{
                padding: '0.6rem 0', background: isAct ? ANG_COL : 'rgba(255,255,255,0.06)',
                border: `1px solid ${isAct ? ANG_COL : BORDER}`, borderRadius: 7,
                color: isAct ? '#fff' : 'rgba(255,255,255,0.55)', fontWeight: 700,
                fontSize: '0.95rem', cursor: disabled ? 'not-allowed' : 'pointer', transition: 'all 0.12s',
              }}>
              {p.toFixed(1)}
            </button>
          );
        })}
      </div>

      {/* ±0.1 Feineinstellung */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem' }}>
        <button disabled={disabled}
          onClick={() => onScoreChange(Math.max(0, parseFloat((score - 0.1).toFixed(1))))}
          style={{ background: 'rgba(255,255,255,0.07)', border: `1px solid ${BORDER}`, borderRadius: 8, width: 44, height: 44, color: '#fff', fontSize: '1.2rem', fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer' }}>
          −
        </button>
        <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.25)' }}>±0.1</span>
        <button disabled={disabled}
          onClick={() => onScoreChange(Math.min(10, parseFloat((score + 0.1).toFixed(1))))}
          style={{ background: 'rgba(255,255,255,0.07)', border: `1px solid ${BORDER}`, borderRadius: 8, width: 44, height: 44, color: '#fff', fontSize: '1.2rem', fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer' }}>
          +
        </button>
      </div>

      <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.2)', textAlign: 'center' }}>
        Angreifer-Qualität · Übung {activeUebung}
      </div>
    </div>
  );
}

/* ── Verteidiger Score-Panel (benannte Person) ── */
function VerteidigerPanel({ level, activeUebung, teilnehmer, selectedId, onSelect,
  scores, savedUebungen, onScoreChange, disabled, gesamtVomServer, uebungen }) {

  const lc = LEVEL_CFG[level];
  const tn = teilnehmer.find(t => t.wettkaempfer_id === selectedId) || null;
  const scoreKey = tn ? `${tn.wettkaempfer_id}_${activeUebung}` : null;
  const currentScore = scoreKey ? (scores[scoreKey] ?? lc.default) : lc.default;
  const isSaved = scoreKey ? !!savedUebungen[scoreKey] : false;

  // Die Gesamtpunkte rechnet der Server; hier wird nur angezeigt. Ersatzweise
  // addiert, solange von dort noch nichts vorliegt.
  const gesamt = !tn
    ? 0
    : (gesamtVomServer?.[tn.wettkaempfer_id] ?? Array.from({ length: uebungen }, (_, i) => i + 1)
        .reduce((s, u) => s + (scores[`${tn.wettkaempfer_id}_${u}`] ?? 0), 0));

  return (
    <div style={{
      background: SURFACE, border: `1px solid ${VERT_BD}`, borderRadius: 14,
      padding: '1.1rem 1.25rem', display: 'flex', flexDirection: 'column', gap: '0.85rem',
      flex: 1, minWidth: 0,
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <span style={{ fontSize: '1.1rem' }}>🛡️</span>
        <span style={{ fontWeight: 700, color: VERT_COL, fontSize: '0.9rem', letterSpacing: '0.04em' }}>VERTEIDIGER</span>
        {isSaved && (
          <span style={{ marginLeft: 'auto', fontSize: '0.72rem', color: GREEN, background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: 5, padding: '0.1rem 0.45rem' }}>
            ✓ Gespeichert
          </span>
        )}
      </div>

      {/* Teilnehmer-Auswahl */}
      <select
        value={selectedId || ''}
        onChange={e => onSelect(e.target.value ? Number(e.target.value) : null)}
        style={{
          background: SURFACE2, border: `1px solid ${VERT_BD}`, borderRadius: 8,
          padding: '0.45rem 0.75rem', color: '#e8e8f0', fontSize: '0.85rem',
          fontWeight: 600, width: '100%', cursor: 'pointer',
        }}
      >
        <option value="">— Verteidiger auswählen —</option>
        {teilnehmer.map(t => (
          <option key={t.wettkaempfer_id} value={t.wettkaempfer_id}>
            {t.vorname} {t.nachname} · {t.verein_name}
          </option>
        ))}
      </select>

      {tn ? (
        <>
          {/* Athlet-Info */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0.75rem', background: VERT_BG, borderRadius: 8, border: `1px solid ${VERT_BD}` }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#fff' }}>
                {tn.vorname} {tn.nachname}
              </div>
              <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)', marginTop: 1 }}>
                {tn.verein_name} · {tn.gurtfarbe || '—'}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Gesamt</div>
              <div style={{ fontSize: '1.6rem', fontWeight: 900, color: VERT_COL, lineHeight: 1 }}>
                {gesamt.toFixed(1)}
              </div>
            </div>
          </div>

          {/* Score-Anzeige */}
          <div style={{ textAlign: 'center', fontSize: '3.5rem', fontWeight: 900, color: VERT_COL, lineHeight: 1, padding: '0.25rem 0' }}>
            {currentScore.toFixed(1)}
          </div>

          {/* Preset-Buttons */}
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${lc.presets.length}, 1fr)`, gap: '0.45rem' }}>
            {lc.presets.map(p => {
              const isAct = Math.abs(p - currentScore) < 0.01;
              return (
                <button key={p} disabled={disabled}
                  onClick={() => onScoreChange(scoreKey, p)}
                  style={{
                    padding: '0.6rem 0', background: isAct ? VERT_COL : 'rgba(255,255,255,0.06)',
                    border: `1px solid ${isAct ? VERT_COL : BORDER}`, borderRadius: 7,
                    color: isAct ? '#fff' : 'rgba(255,255,255,0.55)', fontWeight: 700,
                    fontSize: '0.95rem', cursor: disabled ? 'not-allowed' : 'pointer', transition: 'all 0.12s',
                  }}>
                  {p.toFixed(1)}
                </button>
              );
            })}
          </div>

          {/* ±0.1 Feineinstellung */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem' }}>
            <button disabled={disabled}
              onClick={() => onScoreChange(scoreKey, Math.max(0, parseFloat(((scores[scoreKey] ?? lc.default) - 0.1).toFixed(1))))}
              style={{ background: 'rgba(255,255,255,0.07)', border: `1px solid ${BORDER}`, borderRadius: 8, width: 44, height: 44, color: '#fff', fontSize: '1.2rem', fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer' }}>
              −
            </button>
            <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.25)' }}>±0.1</span>
            <button disabled={disabled}
              onClick={() => onScoreChange(scoreKey, Math.min(10, parseFloat(((scores[scoreKey] ?? lc.default) + 0.1).toFixed(1))))}
              style={{ background: 'rgba(255,255,255,0.07)', border: `1px solid ${BORDER}`, borderRadius: 8, width: 44, height: 44, color: '#fff', fontSize: '1.2rem', fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer' }}>
              +
            </button>
          </div>

          {/* Übungen-Fortschritt */}
          <div style={{ display: 'flex', gap: 3 }}>
            {Array.from({ length: uebungen }, (_, i) => i + 1).map(u => (
              <div key={u} style={{
                flex: 1, height: 5, borderRadius: 3,
                background: savedUebungen[`${tn.wettkaempfer_id}_${u}`] ? VERT_COL : 'rgba(255,255,255,0.1)',
                transition: 'background 0.2s',
              }} />
            ))}
          </div>
        </>
      ) : (
        <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.2)', fontSize: '0.85rem', padding: '2rem 0' }}>
          Verteidiger auswählen
        </div>
      )}
    </div>
  );
}

/* ── Hauptkomponente ── */
export default function SVSchreiberPage() {
  const [searchParams] = useSearchParams();
  const turnierId = searchParams.get('turnierId');
  const initKatId = searchParams.get('kategorieId');
  // Leinwand über den Server (18.10.2026), wie im Formen-Panel
  const leinwandMatte = searchParams.get('matte');
  useEffect(() => { setzeLeinwandZiel(turnierId, leinwandMatte); }, [turnierId, leinwandMatte]);
  const token     = localStorage.getItem('token');

  const [kategorien,    setKategorien]    = useState([]);
  const [selectedKat,   setSelectedKat]   = useState(null);
  // Zahl der Uebungen: steht an der Kategorie (bei SV ist anzahl_kampfrichter
  // die Zahl der Uebungen). Muss vor der ersten Verwendung stehen.
  const UEBUNGEN = Number(selectedKat?.anzahl_kampfrichter) > 0
    ? Number(selectedKat.anzahl_kampfrichter)
    : UEBUNGEN_VORGABE;
  const [teilnehmer,    setTeilnehmer]    = useState([]);
  const [loading,       setLoading]       = useState(true);
  // Kategorie abschließen (21.10.2026), wie im Formen-Panel
  const istAbgeschlossen = selectedKat?.status === 'completed';
  const [abschlussLaeuft, setAbschlussLaeuft] = useState(false);
  const ankuendigungRef = useRef(null);
  // Nächste Kategorie der Matte läuft woanders (Formen, Kampf, Bruchtest)
  const [weiterHinweis, setWeiterHinweis] = useState(null);
  const [aufrufFrage, setAufrufFrage] = useState(null);
  // Sieger der abgeschlossenen Kategorie stehen rund 10 s auf der Tafel,
  // danach kommt die Ankündigung der nächsten von selbst (21.10.2026)
  const SIEGER_VOR_ANKUENDIGUNG_MS = 10000;
  const siegerPauseRef = useRef(null);
  const siegerHaltRef = useRef(false);
  const [siegerPause, setSiegerPause] = useState(null);
  useEffect(() => () => { if (siegerPauseRef.current) clearTimeout(siegerPauseRef.current); }, []);
  const siegerPauseStoppen = () => {
    if (siegerPauseRef.current) clearTimeout(siegerPauseRef.current);
    siegerPauseRef.current = null;
    siegerHaltRef.current = false;
    setSiegerPause(null);
  };
  // Siegerbild aus der frisch geladenen Rangliste (Platz wie Siegerehrung).
  const siegerAufTafel = async (katId, kat) => {
    // Bühne der Siegerehrung (lib/siegerEhrung.js), sonst die Rangliste
    const ehrung = await ladeEhrung({ turnierId, kategorieId: katId, token });
    if (ehrung) {
      siegerHaltRef.current = true;
      sendeAnLeinwand({ typ: 'sieger', disziplin: 'sv', kategorie: kat?.kategorie_name || '', turnier: kat?.turnier_name || '', ehrung, ehrung_id: Date.now() });
      return true;
    }
    try {
      const r = await fetch(`${API_URL}/api/formen/rangliste/${katId}`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.success) return false;
      const fertige = (d.data || []).filter(t => t.status === 'completed')
        .sort((a, b) => ((Number(a.platzierung) || 999) - (Number(b.platzierung) || 999))
          || ((Number(b.gesamt_punkte) || 0) - (Number(a.gesamt_punkte) || 0)));
      if (!fertige.length) return false;
      const podest = fertige.some(t => Number(t.platzierung) > 0)
        ? fertige.filter(t => Number(t.platzierung) > 0 && Number(t.platzierung) <= 3)
        : fertige.slice(0, 3);
      siegerHaltRef.current = true;
      sendeAnLeinwand({ typ: 'sieger', disziplin: 'sv', kategorie: kat?.kategorie_name || '', turnier: kat?.turnier_name || '', rangliste: schlankeRangliste(podest) });
      return true;
    } catch {
      return false;
    }
  };

  const [vertId,        setVertId]        = useState(null);  // Verteidiger wettkaempfer_id
  const [activeUebung,  setActiveUebung]  = useState(1);
  const [level,         setLevel]         = useState('farb');

  // Verteidiger scores: key = `${wkId}_${uebung}`, value = number
  const [vertScores,    setVertScores]    = useState({});
  // Gesamtpunkte, wie der Server sie rechnet. Das Panel addiert nicht mehr
  // selbst: sonst zeigt die Liste hier etwas anderes als Rangliste, Urkunde
  // und Leinwand. Gerechnet wird in backend/utils/formenScoring.js.
  const [serverPunkte,  setServerPunkte]  = useState({});
  const [savedUebungen, setSavedUebungen] = useState({});

  // Angreifer score: ein Wert pro aktiver Übung (anonym, nicht personen-gebunden)
  const lc0 = LEVEL_CFG['farb'];
  const [angScores,     setAngScores]     = useState(
    Object.fromEntries(Array.from({ length: UEBUNGEN }, (_, i) => [`ang_${i + 1}`, lc0.default]))
  );
  const [angSaved,      setAngSaved]      = useState({});  // { `ang_${u}`: true }

  const [saving,        setSaving]        = useState(false);
  const [timerSec,      setTimerSec]      = useState(180);
  const [timerRunning,  setTimerRunning]  = useState(false);
  const timerRef = useRef(null);

  /* ── Timer ── */
  useEffect(() => {
    if (timerRunning && timerSec > 0) {
      timerRef.current = setInterval(() => setTimerSec(p => p - 1), 1000);
    } else if (timerSec === 0) {
      setTimerRunning(false);
    }
    return () => clearInterval(timerRef.current);
  }, [timerRunning, timerSec]);

  /* ── Kategorien laden ── */
  const loadKategorien = useCallback(async () => {
    if (!turnierId) return;
    try {
      const r = await fetch(`${API_URL}/api/formen/sv/${turnierId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (d.success) setKategorien(d.data);
    } catch (e) { console.error(e); }
  }, [turnierId, token]);

  /* ── Kategorie-Details laden ── */
  const loadKategorie = useCallback(async (katId) => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/formen/kategorie/${katId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (d.success) {
        const kat  = d.data.kategorie;
        const list = d.data.teilnehmer || [];
        setSelectedKat(kat);
        setTeilnehmer(list);
        setVertId(null);
        setActiveUebung(1);

        // Vorhandene Verteidiger-Wertungen laden
        const existingScores = {};
        const existingSaved  = {};
        list.forEach(tn => {
          (tn.wertungen || []).forEach(w => {
            const key = `${tn.wettkaempfer_id}_${w.kampfrichter_nummer}`;
            existingScores[key] = parseFloat(w.punkte);
            existingSaved[key]  = true;
          });
        });
        setVertScores(existingScores);
        setSavedUebungen(existingSaved);
        setServerPunkte(Object.fromEntries(
          list.map(tn => [tn.wettkaempfer_id, Number(tn.gesamt_punkte) || 0])
        ));
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [token]);

  /* ── Init ── */
  useEffect(() => {
    const init = async () => {
      await loadKategorien();
      if (initKatId) await loadKategorie(initKatId);
      else setLoading(false);
    };
    init();
  }, [loadKategorien, loadKategorie, initKatId]);

  /* ── Level-Wechsel → Angreifer-Default neu setzen ── */
  useEffect(() => {
    const def = LEVEL_CFG[level].default;
    setAngScores(prev => {
      const next = { ...prev };
      for (let u = 1; u <= UEBUNGEN; u++) {
        if (next[`ang_${u}`] === undefined) next[`ang_${u}`] = def;
      }
      return next;
    });
  }, [level, UEBUNGEN]);   // Kategoriewechsel aendert die Zahl der Uebungen

  /* ── Wertung speichern ── */
  const saveWertung = async () => {
    if (saving || !selectedKat || !vertId) return;
    if (istAbgeschlossen) {
      toast.info('Kategorie ist abgeschlossen. Zum Ändern im Formen-Panel die Wertung des Starters zurücksetzen.');
      return;
    }
    setSaving(true);

    const lc = LEVEL_CFG[level];

    // Verteidiger speichern
    const vertKey  = `${vertId}_${activeUebung}`;
    const vertPkte = vertScores[vertKey] ?? lc.default;

    try {
      const r = await fetch(`${API_URL}/api/formen/wertung`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kategorie_id:    selectedKat.kategorie_id,
          wettkaempfer_id: vertId,
          wertungen: [{ kampfrichter_nummer: activeUebung, punkte: vertPkte, notiz: '' }],
          partial: true,
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.success) toast.fehler('Nicht gespeichert: ' + (d.message || `Fehler ${r.status}`));
      if (r.ok && d.success) {
        setSavedUebungen(prev => ({ ...prev, [vertKey]: true }));
        setAngSaved(prev => ({ ...prev, [`ang_${activeUebung}`]: true }));
        if (d.data?.gesamt_punkte !== undefined) {
          setServerPunkte(prev => ({ ...prev, [vertId]: Number(d.data.gesamt_punkte) || 0 }));
        }

        // Nächste Übung
        if (activeUebung < UEBUNGEN) setActiveUebung(u => u + 1);
        await loadKategorie(selectedKat.kategorie_id);
      }
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  /* ── Leinwand ──
     Dieselbe Anzeige wie bei den Formen (/formen-display), nur mit
     `disziplin: 'sv'`: dort heissen die Kacheln "Übung 1..5" statt "KR 1..5",
     und die Noten werden sofort gezeigt. Anders als bei den Formen sitzen hier
     keine fuenf Kampfrichter, die sich gegenseitig beeinflussen koennten — es
     wertet eine Person Uebung fuer Uebung. */
  const sendeLeinwand = useCallback((zusatz = {}) => {
    if (!selectedKat || siegerHaltRef.current) return;
    const tn = teilnehmer.find(t => t.wettkaempfer_id === vertId) || null;
    const sortiert = [...teilnehmer]
      .map(t => ({ ...t, gesamt_punkte: serverPunkte[t.wettkaempfer_id] ?? t.gesamt_punkte ?? 0 }))
      .sort((a, b) => (Number(b.gesamt_punkte) || 0) - (Number(a.gesamt_punkte) || 0));
    const offene = [...teilnehmer]
      .sort((a, b) => (a.startnummer || 999) - (b.startnummer || 999))
      .filter(t => t.status !== 'completed' && t.wettkaempfer_id !== vertId);
    const fertig = teilnehmer.filter(t => t.status === 'completed').length;
    const noten = Array.from({ length: UEBUNGEN }, (_, i) => {
      const nr = i + 1;
      const wert = tn ? vertScores[`${tn.wettkaempfer_id}_${nr}`] : undefined;
      const gespeichert = tn ? !!savedUebungen[`${tn.wettkaempfer_id}_${nr}`] : false;
      return {
        kampfrichter_nummer: nr,
        punkte: gespeichert && wert !== undefined ? Number(wert).toFixed(1) : null,
        eingegangen: gespeichert,
      };
    });
    const gewertet = noten.filter(n => n.eingegangen).length;
    // Ankündigung der aufgerufenen Kategorie: bis ein Verteidiger gewählt ist
    const ank = ankuendigungRef.current;
    if (ank && tn) ankuendigungRef.current = null;
    const ankuendigung = ank && !tn && String(ank.kategorie_id) === String(selectedKat.kategorie_id)
      ? { kategorie: selectedKat.kategorie_name || '', matte: Number(ank.matte || leinwandMatte) || null, anzahl: teilnehmer.length }
      : undefined;

    sendeAnLeinwand({
      typ: tn ? 'wertung' : 'bereit',
      disziplin: 'sv',
      kategorie: selectedKat.kategorie_name || '',
      turnier: selectedKat.turnier_name || '',
      teilnehmer: schlankerTeilnehmer(tn),
      wertungen: noten,
      rangliste: schlankeRangliste(sortiert),
      scoring: {
        gesamt_punkte: tn ? (serverPunkte[tn.wettkaempfer_id] ?? 0) : 0,
        gestrichen: [],
        vollstaendig: gewertet > 0,
        gross_label: '合計 GESAMT',
        unterzeile: `${gewertet} von ${UEBUNGEN} Übungen gewertet`,
      },
      fortschritt: teilnehmer.length ? { position: Math.min(fertig + 1, teilnehmer.length), gesamt: teilnehmer.length } : null,
      folgende: schlankeFolgende(offene),
      starter: schlankeStarterliste(teilnehmer.map(t => ({ ...t, gesamt_punkte: serverPunkte[t.wettkaempfer_id] ?? t.gesamt_punkte }))),
      ankuendigung,
      ...zusatz,
    });
  }, [selectedKat, teilnehmer, vertId, vertScores, savedUebungen, serverPunkte, UEBUNGEN, leinwandMatte]);

  /* ── Kategorie abschließen (21.10.2026) ──
     Server (POST …/abschliessen): Starter mit allen Übungen über
     services/formenAbschluss.js fertig, Platz, Status 'completed', Zeitplan.
     Danach die nächste SV-Kategorie dieser Matte aufrufen. */
  const kategorieAbschliessen = async () => {
    if (!selectedKat || istAbgeschlossen || abschlussLaeuft) return;
    setAbschlussLaeuft(true);
    const katId = selectedKat.kategorie_id;
    const senden = (bestaetigt) => fetch(`${API_URL}/api/formen/kategorie/${katId}/abschliessen`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ offene_bestaetigt: bestaetigt, matte: leinwandMatte || undefined }),
    });
    try {
      // Rückfrage vorab: hier gibt es kein Prüffenster wie im Formen-Panel.
      if (!window.confirm(`„${selectedKat.kategorie_name}“ abschließen? Danach lässt sie sich nur noch ansehen, und die nächste Kategorie der Matte wird aufgerufen.`)) return;
      let r = await senden(false);
      let d = await r.json().catch(() => ({}));
      if (r.status === 409 && d.code === 'OFFENE_STARTER') {
        const namen = (d.offen || []).map(o => `#${o.startnummer ?? '?'} ${o.name} (${o.noten}/${o.erwartet} Übungen)`).join('\n');
        if (!window.confirm(`Noch ${d.offen.length} Starter ohne alle Übungen:\n${namen}\n\nTrotzdem abschließen? Diese Starter bekommen keinen Platz.`)) return;
        r = await senden(true);
        d = await r.json().catch(() => ({}));
      }
      if (!r.ok || !d.success) { toast.fehler('Nicht abgeschlossen: ' + (d.message || `Fehler ${r.status}`)); return; }
      toast.ok(`„${selectedKat.kategorie_name}“ abgeschlossen.`);
      await loadKategorien();
      const n = d.naechste;
      setWeiterHinweis(null);
      if (!n) {
        await loadKategorie(katId);
        toast.info(d.matte ? `Das war die letzte Kategorie auf Matte ${d.matte}.` : 'Keine Matte bekannt: nächste Kategorie bitte von Hand wählen.', 7000);
        return;
      }
      // Nächste Kategorie der Matte IMMER aufrufen, egal welcher Art
      // (Entscheidung des Veranstalters 21.10.2026), über den Server.
      const aufrufen = async () => {
        const a = await fetch(`${API_URL}/api/formen/kategorie/${katId}/naechste-aufrufen`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ matte: d.matte || leinwandMatte || undefined, schluessel: n.schluessel }),
        });
        const ad = await a.json().catch(() => ({}));
        if (!a.ok || !ad.success) { toast.fehler('Nächste Kategorie nicht aufgerufen: ' + (ad.message || `Fehler ${a.status}`), 9000); return null; }
        if (!ad.aufruf && !ad.laeuft_schon) toast.info(`Kein Aufruf in der Halle: ${ad.aufruf_hinweis || 'unbekannt'}`, 7000);
        return ad;
      };
      // Formen im anderen Panel: erst nach dem Siegerbild aufrufen (das
      // Formen-Panel folgt dem Aufruf und kündigt auf derselben Tafel an)
      const spaeterAufrufen = n.art === 'formen' && n.status !== 'active';
      let ad = spaeterAufrufen ? null
        : n.status === 'active' ? { success: true, laeuft_schon: true, naechste: n, matte: d.matte }
          : await aufrufen();
      if (!spaeterAufrufen && !ad) { await loadKategorie(katId); return; }
      const matte = ad?.matte || d.matte || null;
      const nn = ad?.naechste || n;
      await loadKategorie(katId);
      if (ad?.laeuft_schon) {
        // Nächste läuft schon (nach „Wieder öffnen“ erneut abgeschlossen):
        // nur Hinweis, kein zweiter Aufruf, keine zweite Ankündigung
        setWeiterHinweis({ ...nn, matte, laeuft_schon: true, gleichesPanel: nn.art === 'sv' });
        await siegerAufTafel(katId, selectedKat);
        siegerHaltRef.current = false;
        return;
      }
      if (spaeterAufrufen) setWeiterHinweis({ ...nn, matte, wartet: true });
      else if (nn.art !== 'sv') setWeiterHinweis({ ...nn, matte, aufruf: ad.aufruf, erster_kampf: ad.erster_kampf || null });
      // Tafel (21.10.2026): erst die Sieger (Bühne), dann die Ankündigung.
      const ankuendigen = async () => {
        siegerPauseStoppen();
        if (spaeterAufrufen) {
          ad = await aufrufen();
          if (!ad) return;
          setWeiterHinweis({ ...(ad.naechste || nn), matte: ad.matte || matte, aufruf: ad.aufruf, laeuft_schon: !!ad.laeuft_schon });
          if (ad.laeuft_schon) return;
        }
        if (nn.art === 'sv') {
          ankuendigungRef.current = { kategorie_id: nn.kategorie_id, matte };
          await loadKategorie(nn.kategorie_id);
          await loadKategorien();
          toast.ok(`Weiter mit „${nn.kategorie_name}“.`, 6000);
        } else {
          sendeAnLeinwand({
            typ: 'bereit', disziplin: 'sv', kategorie: nn.kategorie_name || '', turnier: selectedKat.turnier_name || '',
            starter: [], folgende: [],
            ankuendigung: { kategorie: nn.kategorie_name || '', matte: Number(matte || leinwandMatte) || null, anzahl: nn.teilnehmer_anzahl || 0, art: nn.ziel?.art_text || null },
          });
        }
      };
      if (await siegerAufTafel(katId, selectedKat)) {
        siegerPauseRef.current = setTimeout(ankuendigen, SIEGER_ABLAUF_MS + SIEGER_VOR_ANKUENDIGUNG_MS);
        setSiegerPause({ kategorie: nn.kategorie_name, weiter: ankuendigen });
      } else {
        await ankuendigen();
      }
    } catch (e) {
      toast.fehler('Verbindungsfehler beim Abschließen.');
    } finally {
      setAbschlussLaeuft(false);
    }
  };

  /* ── Wieder öffnen (21.10.2026) ── Status zurück, Wertungen bleiben. */
  const wiederOeffnen = async () => {
    if (!selectedKat || !istAbgeschlossen || abschlussLaeuft) return;
    const katId = selectedKat.kategorie_id;
    if (!window.confirm(`„${selectedKat.kategorie_name}“ wieder öffnen?\n\nDie Kategorie steht danach wieder unter „Offen“. Alle Wertungen bleiben erhalten, und Übungen lassen sich wieder ändern. Danach bitte erneut abschließen.`)) return;
    setAbschlussLaeuft(true);
    try {
      const r = await fetch(`${API_URL}/api/formen/kategorie/${katId}/wieder-oeffnen`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.success) { toast.fehler('Nicht wieder geöffnet: ' + (d.message || `Fehler ${r.status}`)); return; }
      siegerPauseStoppen();
      setWeiterHinweis(null);
      await loadKategorien();
      await loadKategorie(katId);
      toast.ok(`„${selectedKat.kategorie_name}“ ist wieder offen.`);
    } catch {
      toast.fehler('Nicht wieder geöffnet: keine Verbindung.');
    } finally {
      setAbschlussLaeuft(false);
    }
  };

  // Bei jeder Aenderung nachziehen: Athletenwechsel, neue Note, neuer Stand.
  useEffect(() => { sendeLeinwand(); }, [sendeLeinwand]);

  const [adresseKopiert, setAdresseKopiert] = useState(false);

  /* ── Gesamt-Punkte eines Verteidigers ──
     Kommen vom Server. Nur solange von dort noch nichts vorliegt (frisch
     getippt, noch nicht gespeichert), wird ersatzweise addiert. */
  const getGesamtVert = (tn) => {
    const vomServer = serverPunkte[tn.wettkaempfer_id];
    if (vomServer !== undefined) return vomServer;
    return Array.from({ length: UEBUNGEN }, (_, i) => i + 1)
      .reduce((s, u) => s + (vertScores[`${tn.wettkaempfer_id}_${u}`] ?? 0), 0);
  };

  // ── Aufruf von außen folgen (21.10.2026), wie im Formen-Panel ──
  const wirdGewertet = !!selectedKat && selectedKat.status !== 'completed' && (
    vertId != null || timerRunning
    || (teilnehmer.some(t => t.status === 'completed' || t.status === 'disqualified')
      && teilnehmer.some(t => t.status !== 'completed' && t.status !== 'disqualified')));
  useAufrufFolgen({
    turnierId, matte: leinwandMatte,
    onNeu: async (aufruf) => {
      if (siegerPauseRef.current) return false;
      if (selectedKat && aufruf.division_code === selectedKat.division_code) return true;
      let liste = [];
      try {
        const r = await fetch(`${API_URL}/api/formen/sv/${turnierId}`, { headers: { Authorization: `Bearer ${token}` } });
        const d = await r.json().catch(() => ({}));
        if (!r.ok || !d.success) return false;
        liste = d.data || [];
      } catch { return false; }
      const kat = liste
        .filter(k => (k.division_code === aufruf.division_code || `formen-${k.kategorie_id}` === aufruf.division_code)
          && k.status !== 'completed' && parseInt(k.teilnehmer_anzahl) > 0)
        .sort((a, b) => Number(a.kategorie_id) - Number(b.kategorie_id))[0];
      if (!kat) return true;
      if (wirdGewertet) { setAufrufFrage({ kat, matte: aufruf.tatami || leinwandMatte }); return true; }
      await svWechseln(kat, aufruf.tatami || leinwandMatte);
      return true;
    },
  });
  const svWechseln = async (kat, matte) => {
    setWeiterHinweis(null);
    setAufrufFrage(null);
    ankuendigungRef.current = { kategorie_id: kat.kategorie_id, matte: matte || null };
    await loadKategorie(kat.kategorie_id);
    await loadKategorien();
    toast.ok(`Weiter mit „${kat.kategorie_name}“.`, 6000);
  };

  const svTyp = getSVSubTyp(selectedKat?.division_code);
  const angScore = angScores[`ang_${activeUebung}`] ?? LEVEL_CFG[level].default;
  const angIsSaved = !!angSaved[`ang_${activeUebung}`];

  if (!turnierId) {
    return (
      <div style={{ background: '#0a0a0f', color: '#fff', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'system-ui, sans-serif' }}>
        <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.4)' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🛡️</div>
          <p>Kein Turnier ausgewählt.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: '#0a0a0f', color: '#e8e8f0', minHeight: '100vh', fontFamily: 'system-ui,-apple-system,sans-serif', display: 'flex', flexDirection: 'column' }}>

      {/* ── Top-Bar ── */}
      <div style={{ background: SURFACE, borderBottom: `1px solid ${BORDER}`, padding: '0.6rem 1.25rem', display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
        <select
          value={selectedKat?.kategorie_id || ''}
          onChange={e => { if (e.target.value) { siegerPauseStoppen(); setAufrufFrage(null); loadKategorie(e.target.value); } }}
          style={{ background: SURFACE2, border: `1px solid ${VERT_BD}`, borderRadius: 7, padding: '0.4rem 0.75rem', color: '#e8e8f0', fontSize: '0.88rem', fontWeight: 600, minWidth: 200, cursor: 'pointer' }}
        >
          <option value="">— Kategorie wählen —</option>
          {/* Zwei Gruppen (21.10.2026): offen und abgeschlossen */}
          {[['Offen', kategorien.filter(k => k.status !== 'completed')], ['Fertig', kategorien.filter(k => k.status === 'completed')]]
            .filter(([, liste]) => liste.length > 0)
            .map(([titel, liste]) => (
              <optgroup key={titel} label={`${titel} (${liste.length})`}>
                {liste.map(k => (
                  <option key={k.kategorie_id} value={k.kategorie_id}>{k.kategorie_name} ({k.division_code})</option>
                ))}
              </optgroup>
            ))}
        </select>

        {selectedKat && !istAbgeschlossen && (
          <button onClick={kategorieAbschliessen} disabled={abschlussLaeuft}
            title="Endwertung speichern, Kategorie unter „Fertig“ ablegen und die nächste Kategorie dieser Matte aufrufen"
            style={{ padding: '0.35rem 0.8rem', background: '#8B0000', border: '1px solid #d4af37', borderRadius: 7, color: '#fff', fontWeight: 700, fontSize: '0.82rem', cursor: abschlussLaeuft ? 'wait' : 'pointer' }}>
            {abschlussLaeuft ? 'Schließt ab …' : 'Kategorie abschließen'}
          </button>
        )}
        {istAbgeschlossen && (
          <span title="Ansehen ja, bewerten nein. Zum Korrigieren: „Wieder öffnen“." style={{ padding: '0.3rem 0.7rem', border: '1px solid rgba(16,185,129,0.5)', borderRadius: 7, color: GREEN, fontWeight: 700, fontSize: '0.8rem' }}>
            Abgeschlossen
          </span>
        )}
        {istAbgeschlossen && (
          <button onClick={wiederOeffnen} disabled={abschlussLaeuft}
            title="Kategorie wieder unter „Offen“ führen. Wertungen bleiben erhalten."
            style={{ padding: '0.35rem 0.8rem', background: 'rgba(255,255,255,0.06)', border: `1px solid ${BORDER}`, borderRadius: 7, color: '#e8e8f0', fontWeight: 700, fontSize: '0.82rem', cursor: abschlussLaeuft ? 'wait' : 'pointer' }}>
            Wieder öffnen
          </button>
        )}

        {selectedKat && (
          <span style={{ fontSize: '0.8rem', background: VERT_BG, border: `1px solid ${VERT_BD}`, borderRadius: 6, padding: '0.25rem 0.65rem', fontWeight: 600, color: VERT_COL }}>
            {svTyp}
          </span>
        )}

        {/* Leinwand: dieselbe Anzeige wie bei den Formen, auch auf einem
            anderen Rechner am Beamer */}
        <button
          onClick={() => oeffneLeinwand()}
          title="Leinwand für das Publikum öffnen"
          style={{ padding: '0.3rem 0.7rem', background: 'rgba(255,255,255,0.04)', border: `1px solid ${BORDER}`, borderRadius: 6, color: 'rgba(255,255,255,0.75)', fontSize: '0.9rem', cursor: 'pointer' }}
        >📺</button>
        <button
          onClick={() => kopiereLeinwandAdresse().then(() => {
            setAdresseKopiert(true);
            setTimeout(() => setAdresseKopiert(false), 2500);
          }).catch(() => {})}
          title="Adresse der Leinwand kopieren — zum Öffnen auf einem anderen Rechner"
          style={{ padding: '0.3rem 0.7rem', background: 'rgba(255,255,255,0.04)', border: `1px solid ${BORDER}`, borderRadius: 6, color: 'rgba(255,255,255,0.75)', fontSize: '0.9rem', cursor: 'pointer' }}
        >{adresseKopiert ? '✅' : '🔗'}</button>

        {/* Level-Wahl */}
        {Object.entries(LEVEL_CFG).map(([key, cfg]) => (
          <button key={key} onClick={() => setLevel(key)}
            style={{ padding: '0.3rem 0.75rem', background: level === key ? VERT_BG : 'rgba(255,255,255,0.04)', border: `1px solid ${level === key ? VERT_BD : BORDER}`, borderRadius: 6, color: level === key ? VERT_COL : 'rgba(255,255,255,0.4)', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' }}>
            {cfg.label}
          </button>
        ))}

        <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.35)', marginLeft: 'auto' }}>
          {teilnehmer.length} Teilnehmer
        </span>

        {/* Timer */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: SURFACE2, border: `1px solid ${BORDER}`, borderRadius: 8, padding: '0.3rem 0.75rem' }}>
          <span style={{ fontSize: '1.1rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: timerSec < 30 ? '#ef4444' : '#e8e8f0' }}>
            {formatTime(timerSec)}
          </span>
          <button onClick={() => setTimerRunning(p => !p)}
            style={{ background: timerRunning ? 'rgba(16,185,129,0.15)' : 'rgba(255,255,255,0.07)', border: `1px solid ${timerRunning ? GREEN : BORDER}`, borderRadius: 5, width: 28, height: 28, color: timerRunning ? GREEN : '#e8e8f0', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 700 }}>
            {timerRunning ? '⏸' : '▶'}
          </button>
          <button onClick={() => { setTimerSec(180); setTimerRunning(false); }}
            style={{ background: 'rgba(255,255,255,0.05)', border: `1px solid ${BORDER}`, borderRadius: 5, width: 28, height: 28, color: 'rgba(255,255,255,0.4)', cursor: 'pointer', fontSize: '0.85rem' }}>
            ↺
          </button>
        </div>
      </div>

      {aufrufFrage && (
        <div className="weiter-hinweis-jp" role="alert">
          <div className="weiter-hinweis-text">
            <strong>Nächste Kategorie „{aufrufFrage.kat.kategorie_name}“ wurde aufgerufen. Wechseln?</strong>
            <span>Hier wird gerade gewertet, darum wechselt das Panel nicht von selbst.</span>
          </div>
          <button className="weiter-hinweis-knopf" onClick={async () => { const f = aufrufFrage; setAufrufFrage(null); await svWechseln(f.kat, f.matte); }}>Wechseln</button>
          <button className="weiter-hinweis-knopf" onClick={() => setAufrufFrage(null)}>Hier bleiben</button>
        </div>
      )}
      {siegerPause && (
        <div className="weiter-hinweis-jp" role="status">
          <div className="weiter-hinweis-text">
            <strong>Die Tafel zeigt die Sieger.</strong>
            <span>In rund {Math.round((SIEGER_ABLAUF_MS + SIEGER_VOR_ANKUENDIGUNG_MS) / 1000)} Sekunden folgt von selbst die Ankündigung: {siegerPause.kategorie}</span>
          </div>
          <button className="weiter-hinweis-knopf" onClick={() => siegerPause.weiter()}>Jetzt ankündigen</button>
        </div>
      )}
      {weiterHinweis && <WeiterHinweis hinweis={weiterHinweis} onSchliessen={() => setWeiterHinweis(null)}
        onWechseln={weiterHinweis.gleichesPanel && weiterHinweis.kategorie_id ? () => {
          const id = weiterHinweis.kategorie_id; setWeiterHinweis(null); siegerPauseStoppen(); loadKategorie(id);
        } : null} />}

      {loading ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.3)', fontSize: '0.9rem' }}>
          Lade Kategorie…
        </div>
      ) : !selectedKat ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '0.75rem', color: 'rgba(255,255,255,0.3)' }}>
          <div style={{ fontSize: '3rem' }}>🛡️</div>
          <p style={{ margin: 0 }}>Bitte eine Kategorie auswählen.</p>
        </div>
      ) : (
        <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 260px', gap: 0, minHeight: 0, overflow: 'hidden' }}>

          {/* ── Scoring-Bereich ── */}
          <div style={{ padding: '1rem 1.25rem', display: 'flex', flexDirection: 'column', gap: '1rem', overflowY: 'auto' }}>

            {/* Übungen-Tabs */}
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {Array.from({ length: UEBUNGEN }, (_, i) => i + 1).map(u => {
                const vertDone = vertId ? !!savedUebungen[`${vertId}_${u}`] : false;
                const active = u === activeUebung;
                return (
                  <button key={u} onClick={() => setActiveUebung(u)}
                    style={{
                      flex: 1, padding: '0.5rem 0',
                      background: active ? VERT_BG : vertDone ? 'rgba(16,185,129,0.1)' : 'rgba(255,255,255,0.04)',
                      border: `1px solid ${active ? VERT_BD : vertDone ? 'rgba(16,185,129,0.4)' : BORDER}`,
                      borderRadius: 8,
                      color: active ? VERT_COL : vertDone ? GREEN : 'rgba(255,255,255,0.35)',
                      fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer', transition: 'all 0.15s',
                    }}>
                    Üb. {u}{vertDone && !active ? ' ✓' : ''}
                  </button>
                );
              })}
            </div>

            {/* Zwei Panels nebeneinander */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <AngreiferPanel
                level={level}
                activeUebung={activeUebung}
                score={angScore}
                onScoreChange={val => setAngScores(prev => ({ ...prev, [`ang_${activeUebung}`]: val }))}
                saved={angIsSaved}
                disabled={saving}
              />
              <VerteidigerPanel
                level={level}
                activeUebung={activeUebung}
                teilnehmer={teilnehmer}
                selectedId={vertId}
                onSelect={setVertId}
                scores={vertScores}
                savedUebungen={savedUebungen}
                onScoreChange={(key, val) => setVertScores(prev => ({ ...prev, [key]: val }))}
                disabled={saving}
                gesamtVomServer={serverPunkte}
                uebungen={UEBUNGEN}
              />
            </div>

            {/* Submit */}
            <button
              onClick={saveWertung}
              disabled={saving || !vertId || istAbgeschlossen}
              style={{
                padding: '0.9rem',
                background: saving ? 'rgba(124,58,237,0.3)' : (!vertId || istAbgeschlossen) ? 'rgba(255,255,255,0.05)' : VERT_COL,
                border: 'none', borderRadius: 10, color: '#fff', fontWeight: 700, fontSize: '1rem',
                cursor: saving || !vertId || istAbgeschlossen ? 'not-allowed' : 'pointer', transition: 'background 0.15s', letterSpacing: '0.02em',
              }}>
              {istAbgeschlossen ? 'Kategorie abgeschlossen: nur ansehen' : saving ? '⏳ Speichere…' : !vertId ? 'Verteidiger auswählen' : `✓ Übung ${activeUebung} werten`}
            </button>

          </div>

          {/* ── Rangliste Sidebar (nur Verteidiger) ── */}
          <div style={{ background: SURFACE, borderLeft: `1px solid ${BORDER}`, padding: '1rem', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
            <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, marginBottom: '0.25rem' }}>
              Rangliste · Verteidiger
            </div>

            {[...teilnehmer]
              .map(tn => ({ ...tn, gesamt: getGesamtVert(tn) }))
              .sort((a, b) => b.gesamt - a.gesamt)
              .map((tn, rank) => {
                const medals = ['🥇', '🥈', '🥉'];
                const isCurrent = tn.wettkaempfer_id === vertId;
                const done = Array.from({ length: UEBUNGEN }, (_, i) => i + 1).every(u => savedUebungen[`${tn.wettkaempfer_id}_${u}`]);

                return (
                  <div key={tn.wettkaempfer_id}
                    onClick={() => { setVertId(tn.wettkaempfer_id); setActiveUebung(1); }}
                    style={{
                      background: isCurrent ? VERT_BG : 'rgba(255,255,255,0.03)',
                      border: `1px solid ${isCurrent ? VERT_BD : BORDER}`,
                      borderRadius: 8, padding: '0.55rem 0.7rem', cursor: 'pointer', transition: 'all 0.12s',
                    }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <span style={{ fontSize: '0.9rem', flexShrink: 0 }}>{medals[rank] || `${rank + 1}.`}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.8rem', color: isCurrent ? VERT_COL : '#e8e8f0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {tn.vorname} {tn.nachname}
                        </div>
                        <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {tn.verein_name}
                        </div>
                      </div>
                      <div style={{ fontWeight: 900, fontSize: '0.95rem', color: done ? GREEN : VERT_COL, flexShrink: 0 }}>
                        {tn.gesamt.toFixed(1)}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 3, marginTop: '0.35rem' }}>
                      {Array.from({ length: UEBUNGEN }, (_, i) => i + 1).map(u => (
                        <div key={u} style={{
                          flex: 1, height: 4, borderRadius: 2,
                          background: savedUebungen[`${tn.wettkaempfer_id}_${u}`] ? VERT_COL : 'rgba(255,255,255,0.1)',
                        }} />
                      ))}
                    </div>
                  </div>
                );
              })}

            {teilnehmer.length === 0 && (
              <p style={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.8rem', textAlign: 'center', margin: 0 }}>
                Keine Teilnehmer
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
